import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import adminAxios from "./adminAxios";

const colors = {
  slate: "#1E293B",
  brand: "#6D9BC3",
  success: "#10B981",
  white: "#ffffff",
  bg: "#F1F5F9",
  text: "#1E293B",
  muted: "#64748B",
  border: "#E2E8F0",
  danger: "#EF4444"
};

function AdminAddProduct() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null); 
  
  // Extract active tenant/admin identity from storage
  const tenantId = localStorage.getItem("adminId") || "global";

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    commissionYield: "", // Matches the backend model field
    specifications: "",  // Matches the backend model field
    rating: "5.0",
    volumeThroughput: "0" // Matches the backend model field
  });
  
  const [images, setImages] = useState([]); 
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    
    if (selectedFiles.length > 0) {
      setImages((prev) => [...prev, ...selectedFiles]);
      const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; 
    }
  };

  const triggerUpload = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index]);
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0) return alert("Please upload at least one product image.");

    setLoading(true);
    try {
      const data = new FormData();
      
      // Append tenant identification to payload
      data.append("adminId", tenantId);
      data.append("tenantId", tenantId);

      // Append text fields
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      
      // Append image files - ensuring key matches Multer 'array' name
      images.forEach((file) => {
        data.append("images", file); 
      });

      const response = await adminAxios.post("/api/products", data, {
        headers: { 
          "Content-Type": "multipart/form-data",
          "x-tenant-id": tenantId 
        }
      });

      if (response.status === 200 || response.status === 201) {
        alert(`SUCCESS: Product added to inventory.`);
        navigate("/admin/dashboard");
      }
    } catch (err) {
      console.error("Submission Error:", err);
      alert(`FAILED: ${err.response?.data?.msg || err.response?.data?.message || "Validation Error"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container} data-tenant-scope={tenantId}>
      <div style={styles.formCard}>
        <div style={styles.header}>
          <h2 style={styles.title}>INVENTORY MANAGEMENT</h2>
          <span style={styles.statusBadge}>TENANT</span>
        </div>
        <p style={styles.subTitle}>Register new assets into the distribution database.</p>

        <form onSubmit={handleSubmit}>
          {/* PRODUCT NAME */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>PRODUCT NAME</label>
            <input 
               name="name" 
               placeholder="e.g. Industrial Transport Container" 
               onChange={handleChange} 
               required 
               style={styles.input} 
            />
          </div>

          {/* FINANCIAL DATA */}
          <div style={styles.row}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>MARKET PRICE ($)</label>
              <input name="price" type="number" step="0.01" placeholder="0.00" onChange={handleChange} required style={styles.input} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>EXPECTED PROFIT ($)</label>
              <input name="commissionYield" type="number" step="0.01" placeholder="0.00" onChange={handleChange} required style={{ ...styles.input, color: colors.success, fontWeight: "bold" }} />
            </div>
          </div>

          {/* PERFORMANCE METRICS */}
          <div style={styles.row}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>RELIABILITY RATING (1-5)</label>
              <input name="rating" type="number" step="0.1" min="1" max="5" placeholder="5.0" onChange={handleChange} required style={styles.input} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>UNITS HANDLED</label>
              <input name="volumeThroughput" type="text" placeholder="e.g. 1,200+" onChange={handleChange} required style={styles.input} />
            </div>
          </div>

          {/* DESCRIPTION */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>TECHNICAL SPECIFICATIONS</label>
            <textarea name="specifications" placeholder="Enter detailed product information..." onChange={handleChange} required style={{ ...styles.input, height: "100px", resize: "none" }} />
          </div>

          {/* MEDIA UPLOAD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>PRODUCT GALLERY</label>
            <input 
                ref={fileInputRef}
                type="file" 
                multiple 
                accept="image/*" 
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
            />
            <button type="button" onClick={triggerUpload} style={styles.uploadTrigger}>
                <span style={{ fontSize: "12px", fontWeight: "bold" }}>CLICK TO UPLOAD IMAGES</span>
                <small style={{ color: colors.muted }}>{images.length} files selected</small>
            </button>
          </div>

          {/* IMAGE PREVIEWS */}
          <div style={styles.previewGrid}>
            {previews.map((url, i) => (
              <div key={i} style={styles.previewWrapper}>
                <img src={url} alt="Preview" style={styles.previewImg} />
                <button type="button" onClick={() => removeImage(i)} style={styles.removeBtn}>REMOVE</button>
              </div>
            ))}
          </div>

          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading ? `SYNCING...` : "ADD PRODUCT TO REGISTRY"}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: { backgroundColor: colors.bg, minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 20px" },
  formCard: { backgroundColor: colors.white, padding: "30px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", width: "100%", maxWidth: "700px", border: `1px solid ${colors.border}` },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  title: { margin: 0, fontSize: "18px", fontWeight: "bold", color: colors.slate },
  statusBadge: { fontSize: "10px", fontWeight: "bold", color: colors.success, backgroundColor: "#E6FFFA", padding: "4px 10px", borderRadius: "4px" },
  subTitle: { fontSize: "12px", color: colors.muted, marginBottom: "25px" },
  inputGroup: { marginBottom: "20px" },
  row: { display: "flex", gap: "15px", marginBottom: "20px" },
  label: { display: "block", marginBottom: "8px", fontSize: "11px", fontWeight: "bold", color: colors.slate },
  input: { width: "100%", padding: "12px", borderRadius: "6px", border: `1px solid ${colors.border}`, fontSize: "14px", boxSizing: "border-box" },
  uploadTrigger: { width: "100%", padding: "20px", border: `2px dashed ${colors.brand}`, backgroundColor: "#F8FAFC", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", cursor: "pointer", borderRadius: "6px" },
  previewGrid: { display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "10px", marginBottom: "20px" },
  previewWrapper: { position: "relative", minWidth: "100px", height: "100px" },
  previewImg: { width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px" },
  removeBtn: { position: "absolute", bottom: 0, width: "100%", background: "rgba(239, 68, 68, 0.9)", color: "#fff", border: "none", fontSize: "10px", padding: "5px 0", cursor: "pointer", borderBottomLeftRadius: "4px", borderBottomRightRadius: "4px" },
  submitBtn: { width: "100%", padding: "15px", backgroundColor: colors.slate, color: "#fff", border: "none", borderRadius: "6px", fontWeight: "bold", cursor: "pointer", fontSize: "14px", transition: "0.2s" }
};

export default AdminAddProduct;